import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Loader from '@components/_common/loader/Loader';
import NoContents from '@components/_common/no-contents/NoContents';
import PullToRefresh from '@components/_common/pull-to-refresh/PullToRefresh';
import CheckInPostStories from '@components/check-in-posts/CheckInPostStories';
import MissionGroupItem from '@components/note/mission-group-item/MissionGroupItem';
import NoteItem from '@components/note/note-item/NoteItem';
import NoteLoader from '@components/note/note-loader/NoteLoader';
import ResponseItem from '@components/response/response-item/ResponseItem';
import { Layout } from '@design-system';
import { useRestoreScrollPosition } from '@hooks/useRestoreScrollPosition';
import { useSWRInfiniteScroll } from '@hooks/useSWRInfiniteScroll';
import { AllPostFeedItem, POST_TYPE } from '@models/post';
import { useBoundStore } from '@stores/useBoundStore';
import { UserSelector } from '@stores/user';
import { getMe } from '@utils/apis/my';
import { logOnboardingEvent } from '@utils/apis/onboardingEvents';
import { isPostsVerQClient } from '@utils/apis/userApiPrefix';
import { MainScrollContainer } from 'src/routes/Root';

function FriendsFeed() {
  const [t] = useTranslation('translation');

  const { scrollRef } = useRestoreScrollPosition('friendsFeed');

  const { fetchCheckIn } = useBoundStore((state) => ({
    myProfile: state.myProfile,
    fetchCheckIn: state.fetchCheckIn,
  }));
  const { featureFlags, myProfile } = useBoundStore(UserSelector);
  const friendFeedKey = isPostsVerQClient(featureFlags?.postsVerQ, myProfile?.current_ver)
    ? '/q/user/feed/'
    : '/user/feed/full';

  const {
    targetRef,
    data: feedItems,
    isLoading,
    isLoadingMore: isFeedItemsLoadingMore,
    mutate: refetchFeed,
  } = useSWRInfiniteScroll<AllPostFeedItem>({
    key: friendFeedKey,
  });

  useEffect(() => {
    logOnboardingEvent('q_feed_opened');
  }, []);

  const handleRefresh = useCallback(async () => {
    await Promise.all([refetchFeed(), fetchCheckIn(), getMe()]);
  }, [refetchFeed, fetchCheckIn]);

  const renderFeedItem = useCallback(
    (item: AllPostFeedItem) => {
      if (item.type === POST_TYPE.NOTE) {
        return <NoteItem key={item.id} note={item} isMyPage={false} refresh={refetchFeed} />;
      }
      if (item.type === POST_TYPE.MISSION_GROUP) {
        return (
          <MissionGroupItem
            key={`mission-group-${item.mission_id ?? item.mission_prompt}`}
            group={item}
            refresh={refetchFeed}
          />
        );
      }
      return (
        <ResponseItem
          key={item.id}
          response={item}
          displayType="FEED"
          isMyPage={false}
          refresh={refetchFeed}
        />
      );
    },
    [refetchFeed],
  );

  return (
    <MainScrollContainer scrollRef={scrollRef} showNotificationPermission>
      <PullToRefresh onRefresh={handleRefresh}>
        <Layout.FlexCol w="100%">
          {featureFlags?.checkInPosts && <CheckInPostStories showCompose />}
          {isLoading ? (
            <NoteLoader />
          ) : feedItems?.[0] && feedItems[0].count > 0 ? (
            <Layout.FlexCol gap={20} ph={16} pt={20} w="100%" style={{ boxSizing: 'border-box' }}>
              {feedItems.map(({ results }) => results?.map((item) => renderFeedItem(item)))}
              <div ref={targetRef} />
              {isFeedItemsLoadingMore && (
                <Layout.FlexRow w="100%" h={40}>
                  <Loader />
                </Layout.FlexRow>
              )}
            </Layout.FlexCol>
          ) : (
            <Layout.FlexRow alignItems="center" w="100%" h="100%">
              <NoContents title={t('no_contents.notes')} />
            </Layout.FlexRow>
          )}
        </Layout.FlexCol>
      </PullToRefresh>
    </MainScrollContainer>
  );
}

export default FriendsFeed;
